/**
 * Canonical auth token storage factory.
 *
 * Creates an instance bound to a single app namespace. All reads, writes,
 * and token-lifecycle operations route through the app's ONE `@puffin-core/storage`
 * `StorageService` instance (N198: injected, never the process-wide singleton) and its
 * `getNamespace(namespace)`, which joins the namespace and internal key names with an
 * underscore (e.g. `palm-auth_token`).
 *
 * @module @puffin-core/auth-token-storage/createAuthTokenStorage
 */

import {
  SESSION_UNREACHABLE_REASONS,
  SessionUnreachableError,
} from '@puffin-core/api-client/session-unreachable';

import { decodeJwtExpiryMs } from './jwtExpiry.js';
import { createRefreshCoordinator } from './refreshCoordinator.js';
import { createExpiryEvents } from './expiryEvents.js';
import { setListViewScope, clearListViewState } from './listViewScope.js';

/** Canonical internal key names. Browser key = `{namespace}_{KEY_*}`. */
const KEY_TOKEN = 'token';
const KEY_CSRF = 'csrfToken';
const KEY_USER = 'user';
const KEY_LOCATIONS = 'locations';

/**
 * The key under which this package USED to persist the refresh token. Nothing writes it any more
 * (UNREACHABLE-MODULE-BURNDOWN-001 unit 3: the refresh token lives only in the server's httpOnly
 * cookie). `clearAuth` still removes it — cleanup of state this package itself wrote, one-way, so
 * a browser that last ran the previous build does not keep a live refresh token readable by any
 * script on the page. Deletable once the deployed fleet is past one refresh TTL (7 days).
 */
const LEGACY_KEY_REFRESH = 'refreshToken';

/** The request header the refresh route's CSRF gate reads (auth-routes `csrfToken.js`). */
const CSRF_HEADER = 'X-CSRF-Token';

/**
 * Whether a refresh's non-2xx answer is the SERVER (or a gateway in front of it) failing — a 5xx, or a 408 — rather
 * than a refusal it served. Every other 4xx is served, 429 included: the refresh route's own limiter answers it with
 * "Too many session refreshes, please sign in again shortly" (auth-routes `tenantAuth.js`).
 *
 * @param {number} status
 * @returns {boolean}
 */
const isServerFailure = (status) => status >= 500 || status === 408;

/**
 * Create an auth token storage instance bound to a single app namespace.
 *
 * Browser localStorage keys produced:
 *   `{namespace}_token`
 *   `{namespace}_csrfToken`
 *   `{namespace}_user`
 *   `{namespace}_locations`
 *
 * 🔴 The REFRESH token is never persisted here. The server sets it as an httpOnly cookie scoped to
 * the auth mount, and a refresh presents that cookie plus the CSRF token bound to it. A refresh
 * token handed to `setAuth` (every minting response still carries one, for palm-native) is
 * ignored: keeping a copy in localStorage would hand it to any script on the page, which is the
 * exposure the cookie exists to remove.
 *
 * @param {string} namespace - Canonical app namespace, e.g. 'pos-auth'
 * @param {{storage: {getNamespace: Function}, clearOnSignOut?: Array<() => void>}} options - `storage`: the web app's
 *   ONE `StorageService` instance, REQUIRED (N198 batch 6: the app builds it with its own logger; this factory never
 *   reaches a process-wide singleton). Its cache and subscribers are the app's, so a token written here is what every
 *   other reader of that instance sees. `clearOnSignOut`: the app's other session-bound browser state, cleared by
 *   `clearAuth` in order after the auth keys — on logout, switch-user, expiry and a served refresh refusal alike
 *   (UAT-BLOCKERS D1 item 11; first, the last good flag read). Fixed at construction.
 * @returns {{
 *   getAccessToken: () => (string | null),
 *   getCsrfToken: () => (string | null),
 *   getUser: () => (object | null),
 *   getUserId: () => (string | null),
 *   getLocations: () => Array,
 *   setAuth: (payload: { accessToken?: string, csrfToken?: string, user?: object, locations?: Array }) => void,
 *   clearAuth: () => void,
 *   isAuthenticated: () => boolean,
 *   getExpiryMs: () => (number | null),
 *   onExpiry: (callback: (detail: object) => void) => () => void,
 *   emitExpiry: (detail?: object) => void,
 *   refreshAccessToken: (endpoint: string, fetchImpl?: typeof fetch) => Promise<string | null>,
 *     — resolves the new token, or `null` when the server REFUSED the refresh (the session is cleared and its end
 *     announced); REJECTS with `SessionUnreachableError` when the server could not be reached (a failed request, a
 *     5xx or 408, an unreadable success) — the session is kept and nothing is announced (U15, owner ruling Q6).
 *   _resetForTest: () => void,
 * }}
 * @throws {Error} if `namespace` is not a non-empty string
 * @throws {TypeError} if `options.storage` is not a `StorageService` instance (it has no `getNamespace`)
 * @throws {TypeError} if `options.clearOnSignOut` is given and is not an array of functions
 */
export function createAuthTokenStorage(namespace, options) {
  if (typeof namespace !== 'string' || namespace.length === 0) {
    throw new Error('createAuthTokenStorage: namespace must be a non-empty string');
  }
  const storage = options && options.storage;
  if (!storage || typeof storage.getNamespace !== 'function') {
    throw new TypeError(
      "createAuthTokenStorage needs `options.storage`: the web app's ONE StorageService instance (it has no " +
        "`getNamespace`). Pass the app's storage — never a process-wide singleton.",
    );
  }

  const clearOnSignOut = options.clearOnSignOut ?? [];
  if (!Array.isArray(clearOnSignOut) || !clearOnSignOut.every((fn) => typeof fn === 'function')) {
    throw new TypeError(
      'createAuthTokenStorage: `options.clearOnSignOut` must be an array of functions',
    );
  }
  // Copied: the clears are fixed at construction, so a caller's later push cannot add one.
  const signOutClears = Object.freeze([...clearOnSignOut]);

  const getNs = () => storage.getNamespace(namespace);
  const refresh = createRefreshCoordinator();
  const events = createExpiryEvents();

  // No catch around the store (SWALLOWED-CATCH-BURNDOWN-001 U3). `StorageService` already absorbs
  // the browser refusing Web Storage (`_safeGetItem` / `_safeSetItem` / `_safeRemoveItem` keep the
  // value in its in-memory cache) and `getJSON` reads a non-JSON value as `null`. What is left to
  // throw here is a DEFECT, and a defect read as "no token" / "no user" looked exactly like a
  // signed-out session.
  const getAccessToken = () => getNs().get(KEY_TOKEN);

  const getCsrfToken = () => getNs().get(KEY_CSRF);

  const getUser = () => getNs().getJSON(KEY_USER);

  /**
   * The signed-in user's id, or null when no one is signed in (a staff app keys its analytics on it, owner ruling
   * "Count by signed-in user (Recommended)", 2026-10-13). Read through `getUser`, so it never throws.
   *
   * @returns {?string}
   */
  const getUserId = () => {
    const user = getUser();
    return user && typeof user.userId === 'string' && user.userId !== '' ? user.userId : null;
  };

  const getLocations = () => {
    const value = getNs().getJSON(KEY_LOCATIONS);
    return Array.isArray(value) ? value : [];
  };

  const setAuth = (payload = {}) => {
    // `refreshToken` is deliberately NOT destructured — see the factory docblock.
    const { accessToken, csrfToken, user, locations } = payload;
    // No catch: a session that could not be stored must never read as one that was (see above).
    const ns = getNs();
    if (accessToken !== undefined && accessToken !== null) {
      ns.set(KEY_TOKEN, accessToken);
    }
    // `undefined` = leave unchanged; `null` = this session has NO refresh credential, so any
    // stored one belongs to a PREVIOUS session and must go (a shared terminal's temporary
    // session must never refresh back into the prior user's).
    if (csrfToken === null) {
      ns.remove(KEY_CSRF);
    } else if (csrfToken !== undefined) {
      ns.set(KEY_CSRF, csrfToken);
    }
    if (user !== undefined && user !== null) {
      ns.setJSON(KEY_USER, user);
      // LVP-001 / PII-GATE-001 #7 — scope persisted list view-state to this
      // user so a shared terminal never bleeds one user's layout to another.
      // Token-only refreshes carry no user → scope is left unchanged.
      if (user.userId) {
        setListViewScope(user.userId);
      }
    }
    if (locations !== undefined && locations !== null) {
      ns.setJSON(KEY_LOCATIONS, locations);
    }
  };

  const clearAuth = () => {
    const ns = getNs();
    ns.remove(KEY_TOKEN);
    ns.remove(KEY_CSRF);
    ns.remove(LEGACY_KEY_REFRESH);
    ns.remove(KEY_USER);
    ns.remove(KEY_LOCATIONS);
    // LVP-001 / PII-GATE-001 #7 — wipe persisted list view-state on logout /
    // switch-user / token expiry so it never outlives the session.
    clearListViewState();
    // The app's other session-bound browser state (UAT-BLOCKERS D1 item 11: its last good flag read), on every path
    // that ends a session. No catch: a clear that throws is a defect, never a clean sign-out.
    signOutClears.forEach((clear) => clear());
  };

  const getExpiryMs = () => decodeJwtExpiryMs(getAccessToken());

  const isAuthenticated = () => {
    const token = getAccessToken();
    if (!token) {
      return false;
    }
    const expMs = decodeJwtExpiryMs(token);
    if (expMs === null) {
      return false;
    }
    return expMs > Date.now();
  };

  const refreshAccessToken = async (endpoint, fetchImpl) =>
    refresh.run(async () => {
      const fetcher = fetchImpl || (typeof fetch === 'function' ? fetch : null);
      if (!fetcher) {
        events.emit({ reason: 'no_fetch' });
        return null;
      }

      const csrfToken = getCsrfToken();
      if (!csrfToken) {
        events.emit({ reason: 'no_csrf_token' });
        return null;
      }

      // The refresh token travels in the httpOnly cookie, never the body: an EMPTY body is what
      // selects the cookie leg server-side (body-first precedence), and the CSRF header is what
      // that leg demands. `same-origin` is stated, not defaulted, because the cookie reaching the
      // route IS the credential.
      let response;
      try {
        response = await fetcher(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', [CSRF_HEADER]: csrfToken },
          body: '{}',
        });
      } catch (cause) {
        // No answer: the session is NOT over — nobody refused it (POS-OFFLINE-STATION-LOCK-001 U15, owner ruling
        // Q6). It is kept, nothing is announced, and the caller is told why (`SessionUnreachableError`).
        throw new SessionUnreachableError({
          endpoint,
          reason: SESSION_UNREACHABLE_REASONS.NO_ANSWER,
          cause,
        });
      }

      if (!response.ok) {
        if (isServerFailure(response.status)) {
          throw new SessionUnreachableError({
            endpoint,
            reason: SESSION_UNREACHABLE_REASONS.SERVER_FAILURE,
            status: response.status,
          });
        }
        clearAuth();
        events.emit({ reason: 'refresh_failed' });
        return null;
      }

      let body;
      try {
        body = await response.json();
      } catch (cause) {
        // A success that is not the route's JSON: something else answered (a captive portal, a proxy page). The
        // refresh route did not, so it refused nothing.
        throw new SessionUnreachableError({
          endpoint,
          reason: SESSION_UNREACHABLE_REASONS.UNREADABLE_ANSWER,
          cause,
        });
      }

      // RES-CLIENT-001 (2026-08-26): was `body && body.data ? body.data : body` — a provenance
      // sniffer. Every host passes one of three refresh endpoints
      // (`/api/v1/auth/{,tenant/,platform/}refresh`), all served by `tenantAuth.js` /
      // `platformAuth.js` in `@puffin-core/auth-routes`, which answer `sendSuccess` exclusively
      // (7/0 and 5/0 bare `res.json`). So the body is always the canonical envelope and the `: body`
      // arm was reachable only on a malformed response — which it would then have inspected for an
      // `accessToken`, i.e. trusted an unreadable body to re-authenticate the session.
      //
      // NARROWED rather than routed through `readEnvelopeData`: the guard below already does the
      // honest thing with a missing payload — clear auth and emit — so the dead arm simply goes.
      //
      // Every refresh ROTATES the refresh token and with it the bound CSRF token, so a success
      // without a string `csrfToken` is malformed exactly as one without an `accessToken` is:
      // storing the access token alone would strand the session one refresh later, with a stale
      // CSRF token the server now refuses.
      const data = body ? body.data : null;
      if (!data || typeof data.accessToken !== 'string' || typeof data.csrfToken !== 'string') {
        clearAuth();
        events.emit({ reason: 'refresh_missing_token' });
        return null;
      }

      setAuth({
        accessToken: data.accessToken,
        csrfToken: data.csrfToken,
      });
      return data.accessToken;
    });

  const _resetForTest = () => {
    clearAuth();
    refresh.reset();
    events.reset();
  };

  return {
    getAccessToken,
    getCsrfToken,
    getUser,
    getUserId,
    getLocations,
    setAuth,
    clearAuth,
    isAuthenticated,
    getExpiryMs,
    onExpiry: events.on,
    emitExpiry: events.emit,
    refreshAccessToken,
    _resetForTest,
  };
}
